import { isAxiosError } from "axios";
import { useCallback, useState } from "react";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import {
    useArchiveReportTemplateMutation,
    useDeleteReportTemplateMutation,
    useSetDefaultReportTemplateMutation,
    useUnarchiveReportTemplateMutation,
} from "@api/reportTemplate.api";

interface LifecycleTemplate<TId extends string | null = string> {
    templateId: TId;
    name: string;
}

export const useReportTemplateLifecycle = () => {
    const { toast, error: errorToast } = useToaster();
    const [deletingTemplate, setDeletingTemplate] =
        useState<LifecycleTemplate | null>(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const { mutateAsync: archiveTemplate } = useArchiveReportTemplateMutation();
    const { mutateAsync: unarchiveTemplate } =
        useUnarchiveReportTemplateMutation();
    const { mutateAsync: deleteTemplate } = useDeleteReportTemplateMutation();
    const { mutateAsync: setDefaultTemplate } =
        useSetDefaultReportTemplateMutation();

    const run = useCallback(
        async ({
            action,
            successMessage,
            failureMessage,
        }: {
            action: () => Promise<void>;
            successMessage: string;
            failureMessage: string;
        }) => {
            try {
                await action();
                toast(successMessage);
                return true;
            } catch (error) {
                if (!isAxiosError(error) || !error.response?.data?.message) {
                    errorToast(failureMessage);
                }
                return false;
            }
        },
        [toast, errorToast],
    );

    const archive = useCallback(
        ({ templateId, name }: LifecycleTemplate) =>
            run({
                action: () => archiveTemplate(templateId),
                successMessage: `Archived template "${name}"`,
                failureMessage: `Failed to archive template "${name}"`,
            }),
        [run, archiveTemplate],
    );

    const enable = useCallback(
        ({ templateId, name }: LifecycleTemplate) =>
            run({
                action: () => unarchiveTemplate(templateId),
                successMessage: `Enabled template "${name}"`,
                failureMessage: `Failed to enable template "${name}"`,
            }),
        [run, unarchiveTemplate],
    );

    const setDefault = useCallback(
        ({ templateId, name }: LifecycleTemplate<string | null>) =>
            run({
                action: () => setDefaultTemplate(templateId),
                successMessage: `Set "${name}" as default`,
                failureMessage: `Failed to set "${name}" as default`,
            }),
        [run, setDefaultTemplate],
    );

    const requestDelete = useCallback((template: LifecycleTemplate) => {
        setDeletingTemplate(template);
        setIsDeleteModalOpen(true);
    }, []);

    const deleteModal = deletingTemplate && (
        <ConfirmationModal
            show={isDeleteModalOpen}
            title={`Delete "${deletingTemplate.name}"?`}
            question="This template will be permanently deleted."
            yes="Delete"
            yesBtnVariant="destructive"
            no="Cancel"
            onReject={() => setIsDeleteModalOpen(false)}
            onHide={() => setIsDeleteModalOpen(false)}
            onConfirm={async () => {
                const { templateId, name } = deletingTemplate;
                const isDeleted = await run({
                    action: () => deleteTemplate(templateId),
                    successMessage: `Deleted template "${name}"`,
                    failureMessage: `Failed to delete template "${name}"`,
                });
                if (isDeleted) {
                    setIsDeleteModalOpen(false);
                }
            }}
        />
    );

    return {
        archive,
        enable,
        setDefault,
        requestDelete,
        deleteModal,
    };
};
